import { useParams, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PageHero from '../../components/PageHero';
import { serviceData } from '../../constants/services';
import { SERVICES } from '../../constants';
import { serviceSchema } from '../../lib/site';

export default function ServicePage() {
  const { slug } = useParams();
  const service = serviceData[slug];

  if (!service) {
    return (
      <div className="frame flex min-h-[70vh] flex-col items-start justify-center gap-6 pt-32">
        <h1 className="display text-5xl">This service page does not exist.</h1>
        <Link to="/services" className="btn-primary">
          See all services
        </Link>
      </div>
    );
  }

  const others = SERVICES.filter((s) => s.link !== `/services/${slug}`);

  return (
    <>
      <SEO
        title={`${service.title} | Farwings Tech Solutions`}
        description={service.metaDescription}
        keywords={`Gujarat, ${service.heroTitle}, agency, services, ${service.technologies.join(', ')}`}
        schema={serviceSchema(slug, service)}
        faq={service.faq}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: service.heroTitle, path: `/services/${slug}` },
        ]}
      />

      <PageHero
        title={service.heroTitle}
        size="sm"
        aside={
          <>
            <p className="lede">{service.heroDescription}</p>
            <ul className="mt-6 flex flex-wrap gap-2" aria-label="Technologies">
              {service.technologies.map((t) => (
                <li key={t} className="rounded-full border border-line px-3 py-1.5 text-sm text-ink">
                  {t}
                </li>
              ))}
            </ul>
          </>
        }
      />

      {service.caseStudy && (
        <section aria-labelledby="case-title" className="frame">
          <div className="grid gap-8 theme-dark rounded-[2rem] bg-base p-8 text-ink sm:p-12 lg:grid-cols-12 lg:items-end">
            <p className="display text-4xl text-accent sm:text-6xl lg:col-span-7">{service.caseStudy.metric}</p>
            <div className="lg:col-span-5">
              <h2 id="case-title" className="text-xl font-bold stretch-semi">
                {service.caseStudy.title}
              </h2>
              <p className="mt-2 text-muted">{service.caseStudy.desc}</p>
            </div>
          </div>
        </section>
      )}

      <section aria-labelledby="method-title" className="frame grid gap-16 py-24 sm:py-32 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <h2 id="method-title" className="display-semi text-4xl sm:text-5xl">
            How we deliver it
          </h2>
          <ol className="mt-10 border-t border-line">
            {service.process.map((step, i) => (
              <li key={step.title} className="grid grid-cols-[3.5rem_1fr] gap-4 border-b border-line py-7">
                <span className="tabular display-semi text-2xl text-accent">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <h3 className="text-xl font-semibold stretch-semi">{step.title}</h3>
                  <p className="mt-2 text-muted">{step.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className="lg:col-span-5">
          <h2 className="display-semi text-4xl sm:text-5xl">Who it is for</h2>
          <ul className="mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-1">
            {service.useCases.map((u) => (
              <li key={u} className="bg-base p-5 text-ink">
                {u}
              </li>
            ))}
          </ul>
          <h2 className="mt-14 display-semi text-3xl">What you get</h2>
          <ul className="mt-6 space-y-3">
            {service.benefits.map((b) => (
              <li key={b} className="flex gap-3 text-ink">
                <span className="mt-2.5 h-1.5 w-4 shrink-0 -skew-x-[24deg] rounded-sm bg-accent" aria-hidden="true" />
                {b}
              </li>
            ))}
          </ul>
          {service.internalLink && (
            <p className="mt-10 border-t border-line pt-6 text-muted">
              {service.internalLink.text}
              <Link to={service.internalLink.href} className="text-accent link-line">
                {service.internalLink.linkText}
              </Link>
              {service.internalLink.postText}
            </p>
          )}
        </div>
      </section>

      <section aria-labelledby="sfaq-title" className="frame grid gap-12 pb-24 sm:pb-32 lg:grid-cols-12">
        <h2 id="sfaq-title" className="display-semi text-4xl sm:text-5xl lg:col-span-5">
          Common questions
        </h2>
        <dl className="border-t border-line lg:col-span-7">
          {service.faq.map((f) => (
            <div key={f.q} className="border-b border-line py-7">
              <dt className="text-lg font-semibold stretch-semi">{f.q}</dt>
              <dd className="mt-2 text-muted">{f.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section aria-labelledby="cta-title" className="frame pb-24 sm:pb-32">
        <div className="rounded-[2rem] border border-line bg-raised p-8 sm:p-14">
          <h2 id="cta-title" className="display text-4xl sm:text-6xl max-w-[16ch]">
            Ready to get this off the ground?
          </h2>
          <p className="mt-6 lede">
            Tell us what you are building. You will get a clear plan, timeline and quote within a few days.
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Link to="/contact" className="btn-primary">
              Start your project
            </Link>
            <Link to="/contact" className="btn-ghost">
              Book a free consultation
            </Link>
          </div>
        </div>
        <nav aria-label="Other services" className="mt-16">
          <h2 className="text-sm text-muted">Other services</h2>
          <ul className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
            {others.map((s) => (
              <li key={s.id}>
                <Link to={s.link} className="display-semi text-xl link-line">
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </section>
    </>
  );
}
